import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { BackHandler, StyleSheet, View } from 'react-native';
import Animated, { Easing, interpolate, runOnJS, useAnimatedStyle, useSharedValue, withTiming, } from 'react-native-reanimated';
import BottomSheet, { BottomSheetView, } from 'utils/packages/bottomSheet';
import { isAndroid } from '../constants';
import ModalBackdrop from './ModalBackdrop';
// react-native-modal's timing and react-native-animatable's default curve
// (CSS `ease`), which the sheet used before.
const TIMING = { duration: 300, easing: Easing.bezier(0.25, 0.1, 0.25, 1) };
const FULL_HEIGHT = ['100%'];
// On Android the sheet's drag took any movement, so a horizontal list in a
// sheet (the emoji picker's) never got the touch. There it starts only on
// vertical movement and gives way once a touch moves sideways. iOS already
// hands sideways swipes to the list. The vertical threshold is kept small so
// the drag still follows the finger almost at once, and the sideways one
// larger so a slightly diagonal swipe down still closes the sheet.
const DRAG_ACTIVE_OFFSET_Y = isAndroid
    ? [-5, 5]
    : undefined;
const DRAG_FAIL_OFFSET_X = isAndroid
    ? [-10, 10]
    : undefined;
/**
 * The sheet behind `design-system/Modal`, animated on the UI thread by
 * @gorhom/bottom-sheet.
 *
 * It is mounted on show and unmounted once it has closed, so every show starts
 * from a fresh sheet. It uses gorhom's plain `BottomSheet`, not
 * `BottomSheetModal`: Modal's hosts already sit at the app root, and the modal's
 * present/dismiss state can get stuck so that `present()` does nothing
 * (gorhom/react-native-bottom-sheet#2669, #2762).
 */
const ModalSheet = ({ visible, children, fade, fullHeight, dismissable, noBackdrop, backdropOpacity, hideBackdropOnClose, onBackdropPress, onSwipeComplete, onBackButtonPress, onShow, onWillHide, onHide, }) => {
    const [rendered, setRendered] = useState(visible);
    if (visible && !rendered) {
        setRendered(true);
    }
    // Remounts the sheet after each close. onHide often shows the next modal
    // (Modal.hide(() => Modal.show(...))), in the same batch as the unmount, so
    // the sheet would otherwise stay mounted at its closed index and never open.
    const [sheetKey, setSheetKey] = useState(0);
    const callbacks = useRef({
        onSwipeComplete,
        onBackButtonPress,
        onShow,
        onWillHide,
        onHide,
    });
    callbacks.current = {
        onSwipeComplete,
        onBackButtonPress,
        onShow,
        onWillHide,
        onHide,
    };
    const sheetRef = useRef(null);
    // True from the start of the close animation until it ends or is reversed.
    const closing = useRef(false);
    // Stands in for the sheet's animatedIndex when fading.
    const fadeIndex = useSharedValue(-1);
    const onOpened = useCallback(() => {
        callbacks.current.onShow?.();
    }, []);
    const onClosed = useCallback(() => {
        closing.current = false;
        setRendered(false);
        setSheetKey((key) => key + 1);
        callbacks.current.onHide();
    }, []);
    useEffect(() => {
        if (!rendered) {
            return;
        }
        if (visible) {
            if (fade) {
                fadeIndex.value = withTiming(0, TIMING, (finished) => {
                    if (finished) {
                        runOnJS(onOpened)();
                    }
                });
            }
            else if (closing.current) {
                sheetRef.current?.snapToIndex(0);
            }
            closing.current = false;
        }
        else if (!closing.current) {
            closing.current = true;
            callbacks.current.onWillHide?.();
            if (fade) {
                fadeIndex.value = withTiming(-1, TIMING, (finished) => {
                    if (finished) {
                        runOnJS(onClosed)();
                    }
                });
            }
            else {
                sheetRef.current?.close();
            }
        }
        // Runs on show and hide only; the shared value is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, rendered]);
    useEffect(() => {
        if (!visible) {
            return undefined;
        }
        // Added last, called first: the sheet shown last gets back first.
        const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
            callbacks.current.onBackButtonPress();
            return true;
        });
        return () => subscription.remove();
    }, [visible]);
    const onAnimate = useCallback((_fromIndex, toIndex) => {
        if (toIndex === -1 && !closing.current) {
            // Closing without a hide: the sheet was swiped down.
            closing.current = true;
            callbacks.current.onWillHide?.();
            callbacks.current.onSwipeComplete();
        }
    }, []);
    const onChange = useCallback((index) => {
        if (index === 0) {
            onOpened();
        }
    }, [onOpened]);
    const renderBackdrop = useCallback((props) => (<ModalBackdrop animatedIndex={props.animatedIndex} style={props.style} visible={visible} opacity={backdropOpacity} hideOnClose={hideBackdropOnClose} onPress={onBackdropPress}/>), [visible, backdropOpacity, hideBackdropOnClose, onBackdropPress]);
    const fadeStyle = useAnimatedStyle(() => ({
        opacity: interpolate(fadeIndex.value, [-1, 0], [0, 1]),
    }));
    if (!rendered) {
        return null;
    }
    if (fade) {
        return (<View style={staticStyles.root} pointerEvents="box-none">
        {!noBackdrop && (<ModalBackdrop animatedIndex={fadeIndex} visible={visible} opacity={backdropOpacity} hideOnClose={hideBackdropOnClose} onPress={onBackdropPress}/>)}
        <Animated.View style={[StyleSheet.absoluteFill, fadeStyle]} pointerEvents="box-none">
          {children}
        </Animated.View>
      </View>);
    }
    // gorhom's plain BottomSheet takes its height from its parent, and Modal's
    // hosts sit after the full-screen app view, so give it the whole screen.
    return (<View style={staticStyles.root} pointerEvents="box-none">
      <BottomSheet key={sheetKey} ref={sheetRef} index={0} snapPoints={fullHeight ? FULL_HEIGHT : undefined} enableDynamicSizing={!fullHeight} enablePanDownToClose={dismissable} enableContentPanningGesture={dismissable} enableOverDrag={false} activeOffsetY={DRAG_ACTIVE_OFFSET_Y} failOffsetX={DRAG_FAIL_OFFSET_X} animationConfigs={TIMING} handleComponent={null} backgroundComponent={null} backdropComponent={noBackdrop ? undefined : renderBackdrop} 
    // gorhom makes the sheet one accessibility element by default, which
    // hides its content from screen readers.
    accessible={false} onAnimate={onAnimate} onChange={onChange} onClose={onClosed}>
        <BottomSheetView style={fullHeight ? staticStyles.fullHeight : undefined}>
          {children}
        </BottomSheetView>
      </BottomSheet>
    </View>);
};
const staticStyles = StyleSheet.create({
    root: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 9999,
    },
    fullHeight: {
        bottom: 0,
    },
});
export default ModalSheet;
